// =====================================================
// BED — Ouverture de l'édition (migration 00293, TICKET-BED-22)
// =====================================================
// Liens du bulletin en masse pour la newsletter d'ouverture, export CSV et
// révocation. Les chiffres, l'éligibilité et l'expiration sont décidés
// SERVEUR (bed_opening_*) ; ce module ne fait qu'appeler et mettre en forme.

import { supabase } from '../supabase';
import { buildBedFormLink } from './admin';

export interface BedOpeningPreview {
  product_id: string;
  product_name: string;
  pricing_confirmed: boolean;
  balance_deadline: string | null;
  /** Expiration visée des liens (fin de journée, heure de Paris). */
  valid_until: string | null;
  in_scope: number;
  eligible: number;
  eligible_manual: number;
  manual_not_400: number;
  link_ok: number;
  to_create: number;
  to_extend: number;
  excluded_derogation: number;
  no_proof: number;
  member_paid: number;
  /** Dernier BPI payé de l'équipage sur une autre édition (changement d'édition). */
  other_edition: number;
  conflicting_amounts: number;
  birth_date_missing: number;
  /** Vague 1 sans aucune adresse email : absents de l'export. */
  eligible_without_email: number;
  /**
   * (00298) Exclus de la vague 1 et équipages à vérifier, avec leur motif.
   * Absents tant que la base n'a pas 00298 : l'écran n'affiche alors que les
   * compteurs.
   */
  exclusions?: BedOpeningListEntry[];
  to_check?: BedOpeningListEntry[];
}

export interface BedOpeningListEntry {
  crew_id: string;
  crew_number: string | null;
  crew_name: string | null;
  /**
   * exclusions : derogation, none, member_paid, other_edition, conflicting_amounts ;
   * to_check : birth_date_missing, manual_not_400, no_email.
   */
  reason: string;
}

export interface BedOpeningBatch {
  valid_until: string;
  processed: number;
  created: number;
  extended: number;
  /** `{ code_erreur: nombre }` : équipages sautés dans ce lot. */
  errors: Record<string, number>;
  next_after_crew_id: string | null;
  done: boolean;
}

export interface BedOpeningExportRow {
  email: string;
  first_name: string | null;
  last_name: string | null;
  role: string;
  crew_number: string | null;
  crew_name: string | null;
  token: string;
  expires_at: string;
  email_crew_count: number;
}

export type BedOpeningError =
  | 'forbidden'
  | 'not_found'
  | 'pricing_not_confirmed'
  | 'no_deadline'
  | 'target_past'
  | 'target_after_event'
  | 'wave_incomplete'
  | 'reason_required'
  | 'error';

export type BedOpeningResult<T> =
  | { status: 'ok'; data: T }
  | { status: BedOpeningError; message?: string };

function classify(message: string | undefined): BedOpeningError {
  if (!message) return 'error';
  if (message.includes('insufficient_privilege')) return 'forbidden';
  if (message.includes('not_found')) return 'not_found';
  if (message.includes('pricing_not_confirmed')) return 'pricing_not_confirmed';
  if (message.includes('no_deadline')) return 'no_deadline';
  if (message.includes('target_past')) return 'target_past';
  if (message.includes('target_after_event')) return 'target_after_event';
  if (message.includes('wave_incomplete')) return 'wave_incomplete';
  if (message.includes('reason_required')) return 'reason_required';
  return 'error';
}

export function bedOpeningErrorMessage(error: BedOpeningError): string {
  switch (error) {
    case 'forbidden':
      return "Vous n'avez pas les droits pour ouvrir le BED.";
    case 'not_found':
      return 'Édition ou équipage introuvable.';
    case 'pricing_not_confirmed':
      return 'La grille tarifaire de cette édition n’est pas confirmée : aucun lien ne peut partir.';
    case 'no_deadline':
      return 'Aucune date butoir du solde sur cette édition : choisissez une date de validité.';
    case 'target_past':
      return 'La date de validité doit être à plus d’un jour.';
    case 'target_after_event':
      return 'La date de validité doit précéder le début de l’événement.';
    case 'wave_incomplete':
      return 'Des liens de la vague 1 manquent ou ont changé depuis l’aperçu : préparez les liens, puis exportez.';
    case 'reason_required':
      return 'Indiquez le motif de la révocation.';
    default:
      return 'Une erreur est survenue.';
  }
}

async function callRpc<T>(
  name: string,
  args: Record<string, unknown>
): Promise<BedOpeningResult<T>> {
  try {
    const { data, error } = await supabase.rpc(name, args);
    if (error) return { status: classify(error.message), message: error.message };
    return { status: 'ok', data: data as T };
  } catch (err) {
    console.error(`[BED-OPENING] ${name}:`, err);
    return { status: 'error' };
  }
}

export function previewBedOpening(
  productId: string,
  validUntil: string | null
): Promise<BedOpeningResult<BedOpeningPreview>> {
  return callRpc('bed_opening_preview', { p_product_id: productId, p_valid_until: validUntil });
}

export function generateBedOpeningBatch(
  productId: string,
  validUntil: string | null,
  afterCrewId: string | null,
  batch = 100
): Promise<BedOpeningResult<BedOpeningBatch>> {
  return callRpc('bed_opening_generate', {
    p_product_id: productId,
    p_valid_until: validUntil,
    p_batch: batch,
    p_after_crew_id: afterCrewId,
  });
}

export function exportBedOpening(
  productId: string,
  validUntil: string | null
): Promise<BedOpeningResult<BedOpeningExportRow[]>> {
  return callRpc('bed_opening_export', { p_product_id: productId, p_valid_until: validUntil });
}

export function revokeBedCrewLinks(
  crewId: string,
  reason: string
): Promise<BedOpeningResult<{ form_links: number; payment_links: number }>> {
  return callRpc('bed_revoke_crew_links', { p_crew_id: crewId, p_reason: reason });
}

const ROLE_LABELS: Record<string, string> = { pilot: 'Pilote', copilot: 'Copilote' };

/** Ligne du CSV d'import Mailjet (clés = en-têtes de colonnes). */
export interface BedOpeningCsvRow {
  email: string;
  prenom: string;
  nom: string;
  role: string;
  numero_equipage: string;
  nom_equipage: string;
  lien_bulletin: string;
  lien_valable_jusqu_au: string;
  email_sur_plusieurs_equipages: string;
}

/**
 * Lignes du CSV : le lien complet est construit ICI, avec la même origine que
 * « Copier le lien actif » (VITE_SITE_4LT_URL, jamais une URL du CRM).
 * Lève BedSiteOriginMissingError si l'origine n'est pas configurée.
 */
export function buildBedOpeningCsvRows(
  rows: BedOpeningExportRow[],
  buildLink: (token: string) => string = buildBedFormLink
): BedOpeningCsvRow[] {
  return rows.map((row) => ({
    email: row.email,
    prenom: row.first_name ?? '',
    nom: row.last_name ?? '',
    role: ROLE_LABELS[row.role] ?? row.role,
    numero_equipage: row.crew_number ?? '',
    nom_equipage: row.crew_name ?? '',
    lien_bulletin: buildLink(row.token),
    lien_valable_jusqu_au: new Date(row.expires_at).toLocaleDateString('fr-FR', {
      timeZone: 'Europe/Paris',
    }),
    email_sur_plusieurs_equipages: row.email_crew_count > 1 ? 'oui' : 'non',
  }));
}
